// NATIVE
import { useEffect } from 'react';

// INSTALLED
import { useDispatch, useSelector } from 'react-redux';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

// CUSTOM
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import InputField from '../../formFields/InputField';
import CheckboxField from '../../formFields/checkBoxField';
import { IDatosBasicos } from '../../../commons/interfaces';
import AutocompleteField from '../../formFields/autocompleteField';
import AutocompleteFieldCountry from '../../formFields/autocompleteFieldCountry';
import AutocompleteFieldDepartament from '../../formFields/AutocompleteFieldDepartament';
import { useNavigate } from 'react-router-dom';

/**
 * DatosBasicosEditar component is responsible for rendering and managing the form for editing basic company data.
 * It utilizes Redux for state management and Formik for form handling.
 *
 * @param {any} props - The properties passed to the component.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @component
 *
 * @example
 * // Example usage:
 * <DatosBasicosEditar formField={formFieldProps} />
 *
 * @remarks
 * This component uses several hooks:
 * - `useDispatch` to dispatch actions to the Redux store.
 * - `useFormikContext` to access Formik context.
 * - `useSelector` to select data from the Redux store.
 *
 * The component also sets form field values based on the company data retrieved from the Redux store.
 *
 * @requires
 * - `useDispatch` from 'react-redux'
 * - `useFormikContext` from 'formik'
 * - `useSelector` from 'react-redux'
 * - `useEffect` from 'react'
 * - `dayjs` for date formatting
 * - Various custom components like `AutocompleteField`, `InputField`, `CheckboxField`, etc.
 */
export const DatosBasicosEditar = (props: any) => {
  // USE DISPATCH
  const dispatch = useDispatch();

  // NAVIGATE
  const navigate = useNavigate();

  // USE FORMIK CONTEXT
  const formikProps: any = useFormikContext();

  // REDUX
  const { company }: IDatosBasicos = useSelector((state: any) => state.company);

  const { country_list_select } = useSelector((state: any) => state.country);
  const { workplace_list_select } = useSelector((state: any) => state.workplace);
  const { municipaly_list_select } = useSelector((state: any) => state.municipalies);
  const { departaments_list_select } = useSelector((state: any) => state.departaments);
  const { type_documents_list_select } = useSelector((state: any) => state.tipodocumento);
  const { economic_activity_list_select } = useSelector((state: any) => state.economicactivity);

  // FORM FIELDS
  const {
    formField: {
      e_type_document,
      e_number_document,
      e_tradename,
      e_business_name,
      e_legal_representative,
      e_economic_activity,
      e_email,
      e_manager_in_charge,
      e_branch_offices,
      e_country,
      e_departament,
      e_municipaly,
      e_address_company,
      e_workplace,
      e_landline,
      e_cell_phone,
    },
  } = props;

  // USE EFFECT
  useEffect(() => {
    //  getDataInputesForm();
  }, []);

  useEffect(() => {
    if (!company || Object.keys(company).length === 0) {
      navigate('/configuracion/compania');
      return;
    }

    setData(company);
  }, [company]);

  useEffect(() => {
    if (!company || Object.keys(company).length == 0) {
      navigate('/configuracion/compania');
    }
  }, []);

  const setData = (company: any) => {
    // ID COMPANY
    formikProps.setFieldValue('companyId', company.id);

    // DATOS BASICOS
    formikProps.setFieldValue('e_type_document', {
      label: company.typeDocument.type_document,
      value: company.typeDocument.id,
    });
    formikProps.setFieldValue('e_number_document', company.number_document);
    formikProps.setFieldValue('e_tradename', company.tradename);
    formikProps.setFieldValue('e_business_name', company.business_name);
    formikProps.setFieldValue('e_legal_representative', company.legal_representative);
    formikProps.setFieldValue('e_economic_activity', {
      label: company.economicActivity.descripcion,
      value: company.economicActivity.id,
    });
    formikProps.setFieldValue('e_email', company.email);
    formikProps.setFieldValue('e_manager_in_charge', company.manager_in_charge);
    formikProps.setFieldValue('e_branch_offices', company.branch_offices);
    formikProps.setFieldValue('e_country', {
      label: company.country.name,
      value: company.country.id,
    });
    formikProps.setFieldValue('e_departament', {
      label: company.departament.name,
      value: company.departament.id,
    });
    formikProps.setFieldValue('e_municipaly', {
      label: company.municipaly.name,
      value: company.municipaly.id,
    });
    formikProps.setFieldValue('e_address_company', company.address_company);
    formikProps.setFieldValue('e_workplace', {
      label: company.workPlace.workplace,
      value: company.workPlace.id,
    });
    formikProps.setFieldValue('e_landline', company.landline);
    formikProps.setFieldValue('e_cell_phone', company.cell_phone);

    // FORMA DE PAGO
    formikProps.setFieldValue('e_payment_frequency', {
      label: company.paymentFrequency.payment_frequency,
      value: company.paymentFrequency.id,
    });
    formikProps.setFieldValue('e_payment_method', {
      label: company.paymentMethod.payment_method,
      value: company.paymentMethod.id,
    });

    if (company.associatedBank && company.associatedBank.associated_bank) {
      formikProps.setFieldValue('e_associated_bank', {
        label: company.associatedBank.associated_bank,
        value: company.associatedBank.id,
      });
    }

    if (company.typeAccountBank && company.typeAccountBank.type_account_bank) {
      formikProps.setFieldValue('e_type_account_bank', {
        label: company.typeAccountBank.type_account_bank,
        value: company.typeAccountBank.id,
      });
    }

    formikProps.setFieldValue('e_account_number', company.account_number);

    const fechaDayjs = dayjs(company.constitution_date);

    // SEGURIDAD SOCIAL
    formikProps.setFieldValue('e_sgsst', { label: company.sgsst.sgsst, value: company.sgsst.id });
    formikProps.setFieldValue('e_arl', { label: company.arl.arl, value: company.arl.id });
    formikProps.setFieldValue('e_constitution_date', fechaDayjs.format());
    formikProps.setFieldValue('e_ley_1429_2010', company.ley_1429_2010);
    formikProps.setFieldValue('e_ley_590_2000', company.ley_590_2000);
    formikProps.setFieldValue('e_ley_1607_2012', company.ley_1607_2012);

    // ARCHIVOS
    formikProps.setFieldValue('e_rut', company.rut);
    formikProps.setFieldValue('e_rut_name_saved_file', company.rut);

    formikProps.setFieldValue('e_chamber_of_commerce', company.chamber_of_commerce);
    formikProps.setFieldValue('e_chamber_of_commerce_name_saved_name', company.chamber_of_commerce);

    formikProps.setFieldValue('e_bank_certificate', company.bank_certificate);
    formikProps.setFieldValue('e_bank_certificate_name_saved_name', company.bank_certificate);

    // LOGOS NOMBRE COMERCIAL
    formikProps.setFieldValue('e_logo_bn_company', company.logo_company);
    formikProps.setFieldValue('e_logo_company_name_saved_file', company.logo_company); // principal en revision

    formikProps.setFieldValue('e_logo_bn_post_company', company.logo_post_company);
    formikProps.setFieldValue('e_logo_post_company_name_saved_file', company.logo_post_company); // principal en revision

    // LOGOS POST COMERCIAL
    formikProps.setFieldValue('e_logo_rz_company', company.logo_bn_rz_company);
    formikProps.setFieldValue('e_logo_rz_company_name_saved_file', company.logo_bn_rz_company);

    formikProps.setFieldValue('e_logo_post_rz_company', company.logo_bn_rz_post_company);
    formikProps.setFieldValue(
      'logo_post_company_rz_name_saved_file',
      company.logo_bn_rz_post_company
    );
  };

  return (
    <div>
      <Typography variant="h5" component="h5" sx={{ margin: 5 }}>
        Editar Compañia
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 3 }}>
          <AutocompleteField
            options={type_documents_list_select}
            name={e_type_document.name}
            label={e_type_document.label}
            element={e_type_document.name}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_number_document.name}
            label={e_number_document.label}
            variant="outlined"
            autoComplete="off"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_tradename.name}
            label={e_tradename.label}
            variant="outlined"
            autoComplete="off"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_business_name.name}
            label={e_business_name.label}
            variant="outlined"
            autoComplete="off"
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: 15 }}>
        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_legal_representative.name}
            label={e_legal_representative.label}
            variant="outlined"
            autoComplete="off"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_email.name}
            label={e_email.label}
            variant="outlined"
            autoComplete="off"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_manager_in_charge.name}
            label={e_manager_in_charge.label}
            variant="outlined"
            autoComplete="off"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <div style={{ display: 'flex', flexDirection: 'row', lineHeight: 0 }}>
            <CheckboxField name={e_branch_offices.name} label={e_branch_offices.label} />
            <Tooltip title="Seleccione si la compañia cuenta con alguna sucursal en caso de no contar con sucursales omita el proceso.">
              <IconButton>
                <HelpOutlineOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: 15 }}>
        <Grid size={{ xs: 12 }}>
          <AutocompleteField
            options={economic_activity_list_select}
            name={e_economic_activity.name}
            label={e_economic_activity.label}
            element={e_economic_activity.name}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: 15 }}>
        <Grid size={{ xs: 4 }}>
          {/* country_list_select */}
          <AutocompleteFieldCountry
            options={country_list_select}
            name={e_country.name}
            label={e_country.label}
            element={e_country.name}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          {/* departaments_list_select */}
          <AutocompleteFieldDepartament
            options={departaments_list_select}
            name={e_departament.name}
            label={e_departament.label}
            element={e_departament.name}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          {/* municipaly_list_select */}
          <AutocompleteField
            options={municipaly_list_select}
            name={e_municipaly.name}
            label={e_municipaly.label}
            element={e_municipaly.name}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: 15 }}>
        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_address_company.name}
            label={e_address_company.label}
            variant="outlined"
            autoComplete="off"
            onBlur={props.handleBlur}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          {/* workplace_list_select */}
          <AutocompleteField
            options={workplace_list_select}
            name={e_workplace.name}
            label={e_workplace.label}
            element={e_workplace.name}
          />
        </Grid>

        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_landline.name}
            label={e_landline.label}
            variant="outlined"
            autoComplete="off"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputField
            name={e_cell_phone.name}
            label={e_cell_phone.label}
            variant="outlined"
            autoComplete="off"
            fullWidth
          />
        </Grid>
      </Grid>
    </div>
  );
};
